/**
 * AchatsDemandesPubliques.js
 * Page ultra professionnelle pour l'achat de demandes publiques COOPER'S
 * Design moderne avec statistiques, filtres avancés et cartes premium
 */

import React from 'react';

import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';

import { ShoppingCart, Building2, Mail, Phone, Calendar, MapPin, Eye, Sparkles, AlertCircle, Lock, Unlock, Package, Recycle, MessageSquare } from 'lucide-react';

const _API_URL = process.env.REACT_APP_BACKEND_URL;

// V37.28 — Activités professionnelles (doit correspondre à la constante
// `ACTIVITES_PROFESSIONNELLES` de GestionTarifsDemandesPubliques.js).
const ACTIVITES_PROFESSIONNELLES = [
  { value: 'btp', label: 'BTP / Construction', icon: '🏗️' },
  { value: 'commerce', label: 'Commerce / Distribution', icon: '🏪' },
  { value: 'industrie', label: 'Industrie / Manufacturing', icon: '🏭' },
  { value: 'agriculture', label: 'Agriculture / Élevage', icon: '🌾' },
  { value: 'services', label: 'Services / Prestations', icon: '🛠️' },
  { value: 'transport', label: 'Transport / Logistique', icon: '🚚' },
  { value: 'restauration', label: 'Restauration / Hôtellerie', icon: '🍽️' },
  { value: 'sante', label: 'Santé / Médico-social', icon: '⚕️' },
  { value: 'education', label: 'Éducation / Formation', icon: '🎓' },
  { value: 'numerique', label: 'Numérique / IT', icon: '💻' },
  { value: 'artisanat', label: 'Artisanat', icon: '🎨' },
  { value: 'tourisme', label: 'Tourisme / Loisirs', icon: '🏝️' },
  { value: 'energie', label: 'Énergie / Environnement', icon: '⚡' },
  { value: 'immobilier', label: 'Immobilier', icon: '🏢' },
  { value: 'finance', label: 'Finance / Assurance', icon: '💰' },
  { value: 'autre', label: 'Autre', icon: '📋' },
];

// V37.28 — Extrait l'array d'activites d'une demande (compat retro string)
const _getDemandeActivites = (d) => {
  if (Array.isArray(d?.activites) && d.activites.length > 0) {
    return d.activites.map(v => (v || '').toString().toLowerCase().trim()).filter(Boolean);
  }
  const raw = (d?.secteur || '').toString().trim().toLowerCase();
  if (!raw || raw === 'general') return [];
  return raw.split(/[,;|]/).map(s => s.trim()).filter(Boolean);
};

export default function AchatsContent({ ctx }) {
  const { activeTab, demandes: demandes, demandesFiltrees, formatDate, formatMontant, getStatutConfig, hasActiveFilters, mesAchats, openRemiseModal, resetFiltres, setActiveTab, setSelectedDemande, setShowAchatModal, setShowDetailsModal, tarifInfo } = ctx;
  return (
    <>
        {/* Content */}
        {activeTab === 'disponibles' ? (
          <div className="space-y-4">
            {demandesFiltrees.length === 0 ? (
              <Card className="bg-white/80 backdrop-blur border-0 shadow-lg">
                <CardContent className="py-16 text-center">
                  <Package className="h-16 w-16 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-slate-700 mb-2">Aucune demande disponible</h3>
                  <p className="text-slate-500">
                    {hasActiveFilters 
                      ? 'Essayez de modifier vos filtres de recherche'
                      : 'De nouvelles demandes seront bientôt disponibles'}
                  </p>
                  {hasActiveFilters && (
                    <Button variant="outline" onClick={resetFiltres} className="mt-4">
                      Réinitialiser les filtres
                    </Button>
                  )}
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {demandesFiltrees.map((demande) => (
                  <Card 
                    key={demande.id} 
                    className="bg-white/90 backdrop-blur border-0 shadow-lg hover:shadow-xl transition-all group overflow-hidden"
                  >
                    <CardContent className="p-0">
                      {/* Header de la carte */}
                      <div className="bg-gradient-to-r from-indigo-500 to-purple-500 p-4 text-white">
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-3">
                            <div className="p-2 bg-white/20 rounded-lg">
                              <Building2 className="h-5 w-5" />
                            </div>
                            <div>
                              <h3 className="font-semibold text-lg">
                                {demande.nom_entreprise || 'Entreprise confidentielle'}
                              </h3>
                              <p className="text-indigo-100 text-sm">
                                {demande.type_demande || demande.type || 'Demande générale'}
                              </p>
                              {demande.oscop_id && (
                                <p
                                  className="text-amber-200 text-[11px] font-mono tracking-wider mt-1"
                                  data-testid={`oscop-id-${demande.id}`}
                                >
                                  Réf. O'SCOP · {demande.oscop_id}
                                </p>
                              )}
                            </div>
                          </div>
                          {/* V37.08 — Badge dynamique : "Nouveau" si <24h, sinon âge en jours */}
                          {(() => {
                            const ageMs = demande.created_at ? Date.now() - new Date(demande.created_at).getTime() : Infinity;
                            const ageHours = ageMs / (1000 * 60 * 60);
                            const ageDays = Math.floor(ageHours / 24);
                            if (ageHours < 24) {
                              return (
                                <Badge className="bg-amber-500 text-white border-0 font-semibold animate-pulse">
                                  <Sparkles className="h-3 w-3 mr-1" />
                                  Nouveau
                                </Badge>
                              );
                            }
                            if (ageDays <= 7) {
                              return (
                                <Badge className="bg-emerald-500 text-white border-0 font-medium">
                                  Il y a {ageDays}j
                                </Badge>
                              );
                            }
                            return null;
                          })()}
                        </div>
                      </div>

                      {/* Corps de la carte */}
                      <div className="p-4 space-y-4">
                        {/* V37.19 — Badges Type + Territoire foncés (cohérence Mes Achats) */}
                        <div className="flex flex-wrap gap-2" data-testid={`disponible-tags-${demande.id}`}>
                          {(demande.type_demande || demande.type) && (
                            <Badge className="bg-indigo-600 hover:bg-indigo-700 text-white border-0 text-[11px] font-bold tracking-wide shadow-sm">
                              {String(demande.type_demande || demande.type).toUpperCase()}
                            </Badge>
                          )}
                          {demande.territoire && (
                            <Badge className="bg-amber-500 hover:bg-amber-600 text-white border-0 text-[11px] font-bold tracking-wide shadow-sm">
                              <MapPin className="h-3 w-3 mr-1" />
                              {demande.territoire}
                            </Badge>
                          )}
                        </div>

                        {/* Informations masquées */}
                        <div className="grid grid-cols-2 gap-3">
                          <div className="flex items-center gap-2 text-slate-600 bg-slate-50 p-2 rounded-lg">
                            <Lock className="h-4 w-4 text-slate-400" />
                            <span className="text-sm">Contact masqué</span>
                          </div>
                          <div className="flex items-center gap-2 text-slate-600 bg-slate-50 p-2 rounded-lg">
                            <Mail className="h-4 w-4 text-slate-400" />
                            <span className="text-sm">Email masqué</span>
                          </div>
                          <div className="flex items-center gap-2 text-slate-600 bg-slate-50 p-2 rounded-lg">
                            <Phone className="h-4 w-4 text-slate-400" />
                            <span className="text-sm">Tél. masqué</span>
                          </div>
                          <div className="flex items-center gap-2 text-slate-700 bg-amber-50 border border-amber-200 p-2 rounded-lg font-medium">
                            <MapPin className="h-4 w-4 text-amber-600" />
                            <span className="text-sm">{demande.territoire || 'France'}</span>
                          </div>
                        </div>

                        {/* Description */}
                        {demande.description && (
                          <p className="text-slate-600 text-sm line-clamp-2 bg-slate-50 p-3 rounded-lg">
                            {demande.description}
                          </p>
                        )}

                        {/* Footer */}
                        <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                          <div className="flex items-center gap-2 text-slate-500 text-sm">
                            <Calendar className="h-4 w-4" />
                            {formatDate(demande.created_at)}
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-lg font-bold text-indigo-600">
                              {formatMontant(demande?.prix_ttc || tarifInfo?.prix_ttc || tarifInfo?.prix_unitaire_ttc || 12.48)}
                            </span>
                            <Button 
                              onClick={() => { setSelectedDemande(demande); setShowAchatModal(true); }}
                              className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white shadow-lg"
                            >
                              <ShoppingCart className="h-4 w-4 mr-2" />
                              Acheter
                            </Button>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        ) : (
          /* Mes Achats */
          <div className="space-y-4">
            {mesAchats.length === 0 ? (
              <Card className="bg-white/80 backdrop-blur border-0 shadow-lg">
                <CardContent className="py-16 text-center">
                  <ShoppingCart className="h-16 w-16 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-slate-700 mb-2">Aucun achat</h3>
                  <p className="text-slate-500 mb-4">
                    Vous n'avez pas encore acheté de demandes
                  </p>
                  <Button 
                    onClick={() => setActiveTab('disponibles')}
                    className="bg-gradient-to-r from-indigo-600 to-purple-600"
                  >
                    <Package className="h-4 w-4 mr-2" />
                    Voir les demandes disponibles
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {mesAchats.map((achat) => {
                  const statutConfig = getStatutConfig(achat.statut);
                  const StatutIcon = statutConfig.icon;
                  const isConfirme = achat.statut === 'paiement_confirme';
                  
                  return (
                    <Card 
                      key={achat.id} 
                      className="bg-white/90 backdrop-blur border-0 shadow-lg hover:shadow-xl transition-all overflow-hidden"
                    >
                      <CardContent className="p-0">
                        {/* Header */}
                        <div className={`p-4 ${isConfirme ? 'bg-gradient-to-r from-emerald-500 to-teal-500' : 'bg-gradient-to-r from-amber-500 to-orange-500'} text-white`}>
                          <div className="flex items-start justify-between">
                            <div className="flex items-center gap-3">
                              <div className="p-2 bg-white/20 rounded-lg">
                                {isConfirme ? <Unlock className="h-5 w-5" /> : <Lock className="h-5 w-5" />}
                              </div>
                              <div>
                                <h3 className="font-semibold text-lg" data-testid={`achat-name-${achat.id || achat.demande_id}`}>
                                  {isConfirme
                                    ? (
                                        // V37.15 — Identité complète : "Prenom Nom — Raison sociale"
                                        [
                                          [achat.demande?.prenom || achat.prenom_contact, achat.demande?.nom || achat.nom_contact].filter(Boolean).join(' ').trim(),
                                          achat.demande?.raison_sociale || achat.demande?.entreprise || achat.demande?.nom_entreprise || achat.nom_entreprise,
                                        ].filter(Boolean).join(' — ') || 'Coordonnées déverrouillées'
                                      )
                                    : 'En attente de confirmation'}
                                </h3>
                                <p className="text-white/80 text-sm">
                                  Acheté le {formatDate(achat.date_achat || achat.created_at)}
                                </p>
                                {(achat.demande?.oscop_id || achat.oscop_id) && (
                                  <p
                                    className="text-white/90 text-[11px] font-mono tracking-wider mt-1"
                                    data-testid={`achat-oscop-id-${achat.demande_id || achat.id}`}
                                  >
                                    Réf. O'SCOP · {achat.demande?.oscop_id || achat.oscop_id}
                                  </p>
                                )}
                              </div>
                            </div>
                            <Badge className={`${statutConfig.color} border`}>
                              <StatutIcon className="h-3 w-3 mr-1" />
                              {statutConfig.label}
                            </Badge>
                          </div>
                        </div>

                        {/* Corps */}
                        <div className="p-4 space-y-4">
                          {isConfirme ? (
                            /* V37.15 — Coordonnées COMPLÈTES déverrouillées (nom, prenom, entreprise, email, tel, adresse, message, type/territoire) */
                            <div className="space-y-3" data-testid={`achat-coordonnees-${achat.id || achat.demande_id}`}>
                              {/* Entreprise / Raison sociale */}
                              <div className="flex items-start gap-3 p-3 bg-emerald-50 rounded-lg">
                                <Building2 className="h-5 w-5 text-emerald-700 flex-shrink-0 mt-0.5" />
                                <div className="min-w-0 flex-1">
                                  <p className="text-[10px] uppercase tracking-wider text-emerald-900 font-bold">Entreprise</p>
                                  <p className="font-semibold text-emerald-950 truncate">
                                    {achat.demande?.raison_sociale || achat.demande?.entreprise || achat.demande?.nom_entreprise || achat.nom_entreprise || '—'}
                                  </p>
                                  {achat.demande?.siret && (
                                    <p className="text-[11px] text-emerald-800 font-mono mt-0.5">SIRET · {achat.demande.siret}</p>
                                  )}
                                </div>
                              </div>

                              {/* Type + Territoire */}
                              {(achat.demande?.type_demande || achat.demande?.territoire) && (
                                <div className="flex flex-wrap gap-2" data-testid={`achat-tags-${achat.id || achat.demande_id}`}>
                                  {achat.demande?.type_demande && (
                                    <Badge className="bg-indigo-600 hover:bg-indigo-700 text-white border-0 text-[11px] font-bold tracking-wide shadow-sm">
                                      {achat.demande.type_demande.toUpperCase()}
                                    </Badge>
                                  )}
                                  {achat.demande?.territoire && (
                                    <Badge className="bg-amber-500 hover:bg-amber-600 text-white border-0 text-[11px] font-bold tracking-wide shadow-sm">
                                      <MapPin className="h-3 w-3 mr-1" />
                                      {achat.demande.territoire}
                                    </Badge>
                                  )}
                                </div>
                              )}

                              {/* Email + Téléphone */}
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                <a
                                  href={`mailto:${achat.demande?.email || achat.email_contact || ''}`}
                                  className="flex items-center gap-2 p-2.5 bg-slate-50 hover:bg-slate-100 rounded-lg transition-colors group"
                                  data-testid={`achat-email-link-${achat.id || achat.demande_id}`}
                                >
                                  <Mail className="h-4 w-4 text-slate-500 group-hover:text-indigo-600" />
                                  <span className="text-sm text-slate-700 truncate">{achat.demande?.email || achat.email_contact || '—'}</span>
                                </a>
                                <a
                                  href={`tel:${achat.demande?.telephone || achat.telephone_contact || ''}`}
                                  className="flex items-center gap-2 p-2.5 bg-slate-50 hover:bg-slate-100 rounded-lg transition-colors group"
                                  data-testid={`achat-tel-link-${achat.id || achat.demande_id}`}
                                >
                                  <Phone className="h-4 w-4 text-slate-500 group-hover:text-indigo-600" />
                                  <span className="text-sm text-slate-700 truncate">{achat.demande?.telephone || achat.telephone_contact || '—'}</span>
                                </a>
                              </div>

                              {/* Adresse (si disponible) */}
                              {(achat.demande?.adresse || achat.demande?.code_postal || achat.demande?.ville || achat.adresse_contact) && (
                                <div className="flex items-start gap-2 p-2.5 bg-slate-50 rounded-lg" data-testid={`achat-adresse-${achat.id || achat.demande_id}`}>
                                  <MapPin className="h-4 w-4 text-slate-500 flex-shrink-0 mt-0.5" />
                                  <span className="text-sm text-slate-700">
                                    {[achat.demande?.adresse || achat.adresse_contact, [achat.demande?.code_postal, achat.demande?.ville].filter(Boolean).join(' ')].filter(Boolean).join(', ') || '—'}
                                  </span>
                                </div>
                              )}

                              {/* Message du client */}
                              {achat.demande?.message && (
                                <div
                                  className="p-3 bg-gradient-to-br from-indigo-100 to-purple-100 border border-indigo-300 rounded-lg"
                                  data-testid={`achat-message-${achat.id || achat.demande_id}`}
                                >
                                  <div className="flex items-center gap-1.5 mb-1.5">
                                    <MessageSquare className="h-3.5 w-3.5 text-indigo-800" />
                                    <p className="text-[10px] uppercase tracking-wider text-indigo-900 font-bold">
                                      Message du client
                                    </p>
                                  </div>
                                  <p className="text-sm text-slate-900 whitespace-pre-wrap leading-relaxed font-medium">
                                    {achat.demande.message}
                                  </p>
                                </div>
                              )}
                            </div>
                          ) : (
                            /* Informations en attente */
                            <div className="text-center p-4 bg-amber-50 rounded-lg">
                              <AlertCircle className="h-8 w-8 text-amber-500 mx-auto mb-2" />
                              <p className="text-amber-700 font-medium">Paiement en cours de traitement</p>
                              <p className="text-amber-600 text-sm mt-1">
                                Les coordonnées seront disponibles après confirmation
                              </p>
                            </div>
                          )}

                          {/* Footer */}
                          <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                            <span className="text-lg font-bold text-slate-700">
                              {formatMontant(achat.montant_ttc)}
                            </span>
                            {isConfirme && (
                              <div className="flex items-center gap-2">
                                <Button
                                  variant="outline"
                                  onClick={() => openRemiseModal(achat)}
                                  className="text-amber-700 border-amber-300 hover:bg-amber-50"
                                  data-testid={`remettre-communityplace-btn-${achat.demande_id}`}
                                >
                                  <Recycle className="h-4 w-4 mr-2" />
                                  Remettre en Communityplace
                                </Button>
                                <Button
                                  variant="outline"
                                  onClick={() => { setSelectedDemande(achat); setShowDetailsModal(true); }}
                                  className="text-emerald-600 border-emerald-200 hover:bg-emerald-50"
                                >
                                  <Eye className="h-4 w-4 mr-2" />
                                  Voir détails
                                </Button>
                              </div>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        )}
    </>
  );
}
